import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdInfo, MdNotifications, MdRefresh } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import PageContent from '../../../shared/layout/containers/PageContent';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { fetchAdminOverviewDashboard } from '../../../services/Http/AdminApi';
import AdminFilterBar from './components/AdminFilterBar';
import FreshnessBadge from './components/FreshnessBadge';
import MetricTrustBadge from './components/MetricTrustBadge';
import MainButton from '../../../shared/layout/buttons/MainButton';
import EmptyState from '../../../shared/layout/feedback/EmptyState';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import DashboardSkeleton from '../../common/skeletons/DashboardSkeleton';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('30d');

    const sections = useMemo(() => ([
        { key: 'headline_kpis', title: 'Headline KPIs' },
        { key: 'platform_health', title: 'Platform Health' },
        { key: 'approval_backlogs', title: 'Approval Backlogs' },
        { key: 'integration_health', title: 'Integration Health' },
        { key: 'revenue_snapshot', title: 'Revenue Snapshot' },
        { key: 'tenant_snapshot', title: 'Tenant Snapshot' },
    ]), []);

    const loadData = useCallback(async (nextPeriod = period) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchAdminOverviewDashboard({ period: nextPeriod });
            setData(response);
        } catch (err) {
            setError(err);
            toast.error(err?.response?.data?.message || 'Failed to load admin overview dashboard');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        loadData(period);
    }, [loadData, period]);

    if (loading) {
        return (
            <PageContainer>
                <PageHeader>
                    <PageTitle>System Admin Dashboard</PageTitle>
                </PageHeader>
                <PageContent>
                    <DashboardSkeleton />
                </PageContent>
            </PageContainer>
        );
    }

    if (error) {
        return <ErrorMessage>Failed to load admin dashboard.</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>System Admin Dashboard</PageTitle>
            </PageHeader>
            <PageContent>
                <AdminFilterBar>
                    <FilterRow>
                        <MetaText>
                            Period: <strong>{data?.period?.label || period}</strong>
                            {data?.meta?.as_of ? ` · As of ${new Date(data.meta.as_of).toLocaleString()}` : ''}
                        </MetaText>
                    </FilterRow>
                    <FilterRow>
                        <PeriodSelect value={period} onChange={(e) => setPeriod(e.target.value)}>
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                        </PeriodSelect>
                        <MainButton size="small" onClick={() => loadData(period)}>
                            <MdRefresh size={16} />
                            Refresh
                        </MainButton>
                        <MainButton size="small" variant="secondary" onClick={() => navigate('/notifications')}>
                            <MdNotifications size={16} />
                            Send notifications
                        </MainButton>
                    </FilterRow>
                </AdminFilterBar>

                {sections.map((section) => (
                    <SectionCard key={section.key}>
                        <SectionHeader>
                            <SectionTitle>{section.title}</SectionTitle>
                        </SectionHeader>
                        {(data?.[section.key] || []).length ? (
                            <StatsGrid>
                                {data[section.key].map((metric) => (
                                    <MetricCardWrap key={`${section.key}-${metric.key}`}>
                                        <MetricMeta>
                                            <MetricTrustBadge tier={metric.trust_tier} />
                                            <FreshnessBadge
                                                status={metric.source_type === 'operational' ? 'success' : 'queued'}
                                                sourceType={metric.source_type}
                                            />
                                        </MetricMeta>
                                        <StatCard
                                            icon={<MdInfo size={26} />}
                                            title={metric.key}
                                            value={formatMetric(metric)}
                                            color={colorForMetric(metric)}
                                        />
                                    </MetricCardWrap>
                                ))}
                            </StatsGrid>
                        ) : (
                            <EmptyState>No data in this section</EmptyState>
                        )}
                    </SectionCard>
                ))}
            </PageContent>
        </PageContainer>
    );
};

const formatMetric = (metric) => {
    const value = metric?.value;
    if (value === undefined || value === null) return '-';
    if (metric.unit === 'currency') {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: metric.currency || 'EUR',
            minimumFractionDigits: 2,
        }).format(Number(value));
    }
    return Number(value).toLocaleString();
};

const colorForMetric = (metric) => {
    const key = (metric?.key || '').toLowerCase();
    if (key.includes('error') || key.includes('failed')) return 'danger';
    if (key.includes('revenue') || key.includes('gmv') || key.includes('billed')) return 'revenueValue';
    if (key.includes('webhook') || key.includes('integrat')) return 'info';
    if (key.includes('pending') || key.includes('backlog')) return 'warning';
    return 'users';
};

export default AdminDashboard;

const SectionCard = styled.section`
    margin-top: 1.25rem;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.surface || '#fff'};
    padding: 1rem;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
`;

const FilterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    flex-wrap: wrap;
`;

const MetaText = styled.div`
    color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
    font-size: 0.875rem;
`;

const PeriodSelect = styled(SimpleSelect)`
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.surface || '#fff'};
    color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
    padding: 0.5rem 0.625rem;
`;

const MetricCardWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const MetricMeta = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-wrap: wrap;
`;
